import {AfterViewInit, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {FormService} from "@core/shared/services/form.service";
import {MatTabGroup} from "@angular/material/tabs";
import {AbstractControl, FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators} from "@angular/forms";
import {FormTabValidationItem, OfferFormTabValidationService} from "@core/shared/services/form/form-tab-validation.service";
import {ActivatedRoute, Router} from "@angular/router";
import {OfferCreatorSearchService} from "@core/shared/services/offer-creator/offer-creator-search.service";
import {Society} from "@core/shared/models/society";
import {map, tap} from "rxjs/operators";
import {Currency} from "@core/shared/models/currency";
import {Observable, of} from "rxjs";
import {CurrencyService} from "@core/shared/services/currency.service";
import {TranslationService} from "@core/shared/services/translation.service";
import {CustomerTypology} from "@core/shared/models/customer-typology";
import {User} from "@core/shared/models/user";
import {UserService} from "@core/shared/services/user.service";
import {uniqueArrayOfObjectsByProperty} from "@core/shared/utils/arrays_utils";
import {OfferAttribute} from "@core/shared/models/offer-attribute";
import {ArrayFilterField} from "@core/shared/models/filter/array-filter-field";
import {OfferAttributeService} from "@core/shared/services/offer-attribute.service";
import {Moment} from "moment/moment";
import {QuotationFlexibilityService} from "@core/shared/services/quotation/quotation-flexibility.service";
import {QuotationFlexibility} from "@core/shared/models/quotation/quotation-flexibility";
import {OfferDurationService} from "@core/shared/services/offer/offer-duration.service";
import {OfferDuration} from "@core/shared/models/offer/offer-duration";
import {REGEX_ONLY_NUMBER} from "@core/shared/models/regex";
import {Country} from "@core/shared/models/country";
import {CountryService} from "@core/shared/services/country.service";
import {Address} from "@core/shared/models/address";
import moment from "moment";
import {DATE_FORMAT, SUPPORTED_LOCALES} from "@app/data";
import {OfferAttributeTypeTagType} from "@core/shared/models/offer-attribute-type";
import {Quotation} from "@core/shared/models/quotation";
import {QuotationService} from "@core/shared/services/quotation.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ConfirmDialogComponent} from "@lib/confirm-dialog/confirm-dialog.component";
import {TranslateService} from "@ngx-translate/core";
import {ConfirmDialogData} from "@lib/confirm-dialog/confirm-dialog";
import {MatOption} from "@angular/material/core";
import {OfferCreatorFilterKey} from "@core/components/offer-creator/offer-creator-filter/offer-creator-filter.component";
import {Pagination} from "@core/shared/models/pagination";
import {OfferCreatorService} from "@core/shared/services/offer-creator.service";
import {OfferCreatorCardService} from "@core/shared/services/offer-creator/offer-creator-card.service";
import {FormPanel} from "@core/shared/models/form/form-panel";
import {SelectGroupComponent, SelectGroupConfiguration} from "@core/components/select/select-group/select-group.component";

interface AttributeObservableData {

    tag: OfferAttributeTypeTagType;

    observable: Observable<OfferAttribute[]>;
}

type TemplateView = ('form' | 'confirmation');

type FormPanelTag = ('date' | 'composition' | 'budget' | 'wish' | 'assurance' | 'more');

@Component({
    selector: 'app-core-page-quotation-create',
    templateUrl: './page-quotation-create.component.html',
    styleUrls: ['./page-quotation-create.component.scss'],
    providers: [
        FormService,
        OfferFormTabValidationService,
        OfferCreatorSearchService,
        OfferCreatorCardService
    ],
    standalone: false
})
export class PageQuotationCreateComponent implements OnInit, AfterViewInit {

    @ViewChild('tabGroup', {static: false}) tabGroup: MatTabGroup;

    @ViewChild('offerCreators', {static: false}) offerCreatorsRef: TemplateRef<any>;

    @ViewChild('formData', {static: false}) formDataRef: TemplateRef<any>;

    @ViewChild('summary', {static: false}) summaryRef: TemplateRef<any>;

    @ViewChild(SelectGroupComponent, {static: false}) regionSelectGroupComponent: SelectGroupComponent<OfferAttribute>;

    public readonly moment = moment;

    public activeViews: TemplateView[] = ['form'];

    public currentUser: User;

    public origin: { key: string, backLabel: string } = null;

    public tabItems: { tag: string, label: string, template: TemplateRef<any> }[] = [];

    public panels: FormPanel<FormPanelTag>[] = [];

    public currencies$: Observable<Currency[]>;

    public flexibilities$: Observable<QuotationFlexibility[]>;

    public durations$: Observable<OfferDuration[]>;

    public countries$: Observable<Country[]>;

    public attributeObservables$: AttributeObservableData[];

    public regionSelectConfiguration: SelectGroupConfiguration<OfferAttribute>;

    public regionSelectSourceCallback: () => Observable<OfferAttribute[]>;

    public regions: OfferAttribute[] = [];

    public availableCustomerTypologies: CustomerTypology[] = [];

    public minDateStart: Date;

    public dateStart: Moment;

    public dateEnd: Moment;

    public minClosedAt: Date;

    public maxClosedAt: Date;

    public maxLengthComment: number = 500;

    public spokenLanguages: string[] = SUPPORTED_LOCALES;

    public otherSpokenLanguagesIdentifier: string = '_OTHER_';

    public minStayDateEnd: Moment = null;

    public maxOfferCreatorSelectionAllowed: number = null;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _dialog: MatDialog,
        private _translateService: TranslateService,
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _formBuilder: FormBuilder,
        private _userService: UserService,
        private _quotationFlexibilityService: QuotationFlexibilityService,
        private _currencyService: CurrencyService,
        private _countryService: CountryService,
        private _offerCreatorService: OfferCreatorService,
        private _offerAttributeService: OfferAttributeService,
        private _offerDurationService: OfferDurationService,
        private _quotationService: QuotationService,
        public formService: FormService,
        public offerCreatorSearchService: OfferCreatorSearchService,
        public offerFormTabValidationService: OfferFormTabValidationService,
        public translationService: TranslationService
    ) {}

    ngOnInit() {

        this.currentUser = this._userService.currentUser.getValue();

        this.minDateStart = moment().add(1, 'days').toDate();

        this.minClosedAt = moment().toDate();

        this.maxClosedAt = moment().add(60, 'days').toDate();

        this._configureSearchService();

        this._initOrigin();

        this._initRegions();

        this._initRegionSelect(this._activatedRoute.snapshot.data['offerCreator'] || null);

        this._initFlexibilities();

        this._initDurations();

        this._initCurrencies();

        this._initCountries();

        this._initAttributes();

        this._initForm();

        this._initEvents();

        this._initPanels();
    }

    ngAfterViewInit() {

        this._initTabItems();

        this._initFormTabValidation();

        this._changeDetectorRef.detectChanges();
    }

    private _configureSearchService(): void {

        this.offerCreatorSearchService.sessionEnabled = true;

        Object.assign(this.offerCreatorSearchService.enabledFilters, {
            [OfferCreatorFilterKey.Destination]: true,
            [OfferCreatorFilterKey.Target]: true,
            [OfferCreatorFilterKey.NetworkOfferCreator]: true,
            [OfferCreatorFilterKey.OfferType]: true,
            [OfferCreatorFilterKey.Name]: true,
            [OfferCreatorFilterKey.Types]: true,
            [OfferCreatorFilterKey.SpokenLanguages]: true,
            [OfferCreatorFilterKey.InCatalog]: this._userService.hasOneOfThisRoles(this.currentUser, ['ROLE_OFFER_DISTRIBUTOR', 'ROLE_INSTITUTIONAL', 'ROLE_FEDERATION']),
            [OfferCreatorFilterKey.HasRseLabel]: true,
        } as { [p in OfferCreatorFilterKey]: boolean });

        this.offerCreatorSearchService.selectOfferCreatorEnabled = true;

        this.offerCreatorSearchService.selectOfferCreatorAllowed = true;

        this.offerCreatorSearchService.unselectOfferCreatorAllowed = true;

        this.offerCreatorSearchService.isOfferCreatorSelectionAllowedCallback = (offerCreator: Society): boolean => {

            return (offerCreator.details.customerTypologies.length > 0) && (offerCreator.details.regions.length > 0 || offerCreator.details.allRegions);
        };

        this.offerCreatorSearchService.offerCreatorSelectionDisableReasonCallback = (offerCreator: Society): string => {

            if(!this.offerCreatorSearchService.selectOfferCreatorAllowed && this.maxOfferCreatorSelectionAllowed !== null){

                return this._translateService.instant('offerCreator.selection.maxAllowed.description.value', { count: this.maxOfferCreatorSelectionAllowed });
            }

            if(((offerCreator.details.customerTypologies.length < 1) || (offerCreator.details.regions.length < 1 && !offerCreator.details.allRegions))){

                return this._translateService.instant('offerCreator.selection.incorrectConfiguration.description.value');
            }
        };

        this.offerCreatorSearchService.isOfferCreatorHighlightedCallback = (offerCreator: Society): boolean => {

            return !!this.offerCreatorSearchService.selectedOfferCreators$.value.find((item: Society): boolean => {

                return offerCreator.id === item.id;
            });
        };

        this.offerCreatorSearchService.offerCreatorHighlightColor = (): string => {

            return 'green';
        };

        this.offerCreatorSearchService.additionalFilterParams$.next([
            `id[andnin][]=${this.currentUser.society.id}`
        ]);
    }

    private _initOrigin(): void {

        this.origin = this._activatedRoute.snapshot.queryParams['origin'] ? {
            key: this._activatedRoute.snapshot.queryParams['origin'],
            backLabel: `quotation.creation.origin.${ this._activatedRoute.snapshot.queryParams['origin'] }.value`
        } : null;
    }

    private _initTabItems(): void {

        this.tabItems = [
            {
                tag: 'offerCreators',
                label: 'offerCreator.plural.selection.value',
                template: this.offerCreatorsRef
            },
            {
                tag: 'formData',
                label: 'form.value',
                template: this.formDataRef
            },
            {
                tag: 'summary',
                label: 'summaryAndSend.value',
                template: this.summaryRef
            }
        ];
    }

    private _initFormTabValidation(): void {

        this.offerFormTabValidationService.init(this.tabGroup, this.form, this.formTabValidationItemsCallback);
    }

    private _initRegions(): void {

        const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', 'region');

        this._offerAttributeService.getItemsAPI([typeFilter.serialize]).subscribe((items: OfferAttribute[]): void => {

            this.regions = items;
        });
    }

    private _initRegionSelect(offerCreator: Society): void {

        this.regionSelectConfiguration = {
            label: this._translateService.instant('destination.plural.value'),
            valuesCallback:(): OfferAttribute[] => {

                return this.configurationControl.get('regions').value;
            },
            optionLabelCallback: (option: OfferAttribute): string => {

                return `${this.translationService.getFallbackTranslation(option.translations).label}`;
            },
            selectedCallback: (option: OfferAttribute): boolean => {

                const items: OfferAttribute[] = [...this.configurationControl.get('regions').value];

                return items.some((item: OfferAttribute): boolean => {

                    return item.id === option.id;
                });
            },
            handleSelectionCallback: (option: OfferAttribute, selected: boolean): void => {

                const items: OfferAttribute[] = [...this.configurationControl.get('regions').value];

                if(selected){

                    items.push(option);
                }
                else{

                    const index: number = items.findIndex((item: OfferAttribute): boolean => {

                        return item.id === option.id;
                    });

                    items.splice(index, 1);
                }

                this.configurationControl.get('regions').patchValue(items);

                this.configurationControl.get('regions').markAsTouched();
            },
            compareOptionsCallback: (a: OfferAttribute, b: OfferAttribute): boolean => {

                if(!a || !b){

                    return false;
                }

                return a.id === b.id;
            },
            retrieveChildOptionsFromParentCallback: (option: OfferAttribute): OfferAttribute[] => {

                return option.children;
            },
            allOptionsSelectionEnabledCallback: (): boolean => {

                return false;
            }
        };

        this.regionSelectSourceCallback = (): Observable<OfferAttribute[]> => {

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', 'region');

            return this._offerAttributeService.getItemsAPI([typeFilter.serialize]).pipe(
                map((offerAttributes: OfferAttribute[]): OfferAttribute[] => {

                    if(!offerCreator || offerCreator.details.allRegions){

                        return offerAttributes;
                    }

                    return offerAttributes.filter((offerAttribute: OfferAttribute): boolean => {

                        return offerCreator.details.regions.some((item: OfferAttribute): boolean => {

                            return item.id === offerAttribute.id;
                        });
                    });
                }),
                tap((): void => {

                    if(offerCreator){

                        this.offerCreatorSearchService.selectOfferCreator(offerCreator);
                    }
                }
            ));
        };
    }

    private _initFlexibilities(): void {

        this.flexibilities$ = this._quotationFlexibilityService.getItemsAPI();
    }

    private _initDurations(): void {

        const params: string[] = [
            `sort[position]=ASC`
        ];

        this.durations$ = this._offerDurationService.getItemsAPI(params);
    }

    private _initCurrencies(): void {

        this.currencies$ = this._currencyService.getItemsAPI().pipe(
            tap((currencies: Currency[]): void => {

                const defaultCurrency: Currency = currencies.find((currency: Currency): boolean => {

                    return currency.code === 'EUR';
                });

                this.pricingControl.get('currency').patchValue(defaultCurrency);
            }),
            map((currencies: Currency[]): Currency[] => {

                return currencies.filter((currency: Currency): boolean => {

                    return !['TWD','AED'].includes(currency.code);
                });
            })
        );
    }

    private _initCountries(): void {

        this.countries$ = this._countryService.getItemsAPI().pipe(
            tap((countries: Country[]): void => {

                const address: Address = this.currentUser.society.addresses.find((address: Address): boolean => {

                    return address.type === 'mailing';
                });

                if(!address || !address.country){

                    return;
                }

                const defaultCountry: Country = countries.find((country: Country): boolean => {

                    return country.code === address.country;
                });

                this.customerControl.get('country').patchValue(defaultCountry);
            })
        );
    }

    private _initAttributes(): void {

        this.attributeObservables$ = [];

        this.attributeObservables$.push(...this.attributeTypeTags.map((tag: OfferAttributeTypeTagType): AttributeObservableData => {

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', tag);

            return {
                tag: tag,
                observable: this._offerAttributeService.getItemsAPI([
                    typeFilter.serialize
                ])
            };
        }));
    }

    private _initForm(): void {

        this.formService.form = this._formBuilder.group({
            offerCreators: [[], [(control: UntypedFormControl) => {

                if (control.value.length < 1) {

                    return {
                        'minLengthArray': {
                            valid: false
                        }
                    };
                }

                if (this.maxOfferCreatorSelectionAllowed !== null && control.value.length > this.maxOfferCreatorSelectionAllowed) {

                    return {
                        'maxLengthArray': {
                            valid: false
                        }
                    };
                }

                return null;
            }]],
            ownerReference: [null],
            closedAt: [null, [Validators.required]],
            composition: this._formBuilder.group({
                flexibility: [null, [(control: UntypedFormControl) => {

                    if(!this.form || !this.configurationControl){

                        return null;
                    }

                    const allowGiftVoucherControl: FormControl = this.configurationControl.get('allowGiftVoucher') as FormControl;

                    const allowGiftVoucher: boolean = allowGiftVoucherControl.value;

                    if(!allowGiftVoucher){

                        return (control.value as boolean) ? null : {
                            'isRequired': {
                                valid: false
                            }
                        };
                    }
                    else{

                        return null;
                    }
                }]],
                duration: [null, [Validators.required]],
                dateStart: [null, [(control: UntypedFormControl) => {

                    if(!this.form || !this.configurationControl){

                        return null;
                    }

                    const allowGiftVoucherControl: FormControl = this.configurationControl.get('allowGiftVoucher') as FormControl;

                    const allowGiftVoucher: boolean = allowGiftVoucherControl.value;

                    if(!allowGiftVoucher){

                        return (control.value as boolean) ? null : {
                            'isRequired': {
                                valid: false
                            }
                        };
                    }
                    else{

                        return null;
                    }
                }]],
                dateEnd: [null, [(control: UntypedFormControl) => {

                    if(!this.form || !this.configurationControl){

                        return null;
                    }

                    const allowGiftVoucherControl: FormControl = this.configurationControl.get('allowGiftVoucher') as FormControl;

                    const allowGiftVoucher: boolean = allowGiftVoucherControl.value;

                    if(!allowGiftVoucher){

                        return (control.value as boolean) ? null : {
                            'isRequired': {
                                valid: false
                            }
                        };
                    }
                    else{

                        return null;
                    }
                }]],
                nbAdult: [null, [Validators.pattern(REGEX_ONLY_NUMBER), (control: UntypedFormControl) => {

                    if(!this.form || !this.compositionControl){

                        return null;
                    }

                    return (control.value as boolean) ? null : {
                        'isRequired': {
                            valid: false
                        }
                    };
                }]],
                nbChild: [null, [Validators.pattern(REGEX_ONLY_NUMBER), (control: UntypedFormControl) => {

                    if(!this.form || !this.compositionControl){

                        return null;
                    }

                    return (control.value as boolean) ? null : {
                        'isRequired': {
                            valid: false
                        }
                    };
                }]],
                childrenAgeComment: [null, [Validators.maxLength(this.maxLengthComment)]]
            }),
            configuration: this._formBuilder.group({
                regions: [[], [(control: UntypedFormControl) => {

                    if (control.value.length < 1) {

                        return {
                            'minLengthArray': {
                                valid: false
                            }
                        };
                    }

                    return null;
                }]],
                regionComment: [null, [Validators.maxLength(this.maxLengthComment)]],
                accommodations: [[], [(control: AbstractControl): { [key: string]: any } => {

                    if(!this.nightIncluded) {

                        return null;
                    }

                    if (control.value.length >= 1) {

                        return null;
                    }

                    return {
                        'minLengthArray': {
                            valid: false
                        }
                    };
                }]],
                activities: [[]],
                restoration: [null, [Validators.required]],
                restorationComment: [null, [Validators.maxLength(this.maxLengthComment)]],
                transportComment: [null, [Validators.maxLength(this.maxLengthComment)]],
                customerTypology: [null, [Validators.required]],
                allowGiftVoucher: [false, [(control: UntypedFormControl) => {

                    if(!control.parent){

                        return null;
                    }

                    const customerTypologyControl: FormControl = control.parent.get('customerTypology') as FormControl;

                    if(!customerTypologyControl || !customerTypologyControl.value){

                        return null;
                    }

                    const customerTypology: CustomerTypology = customerTypologyControl.value;

                    if(customerTypology.type === 'I'){

                        return (control.value !== null) ? null : {
                            'isRequired': {
                                valid: false
                            }
                        };
                    }
                    else {

                        return null;
                    }
                }]],
                accommodationsComment: [null, [Validators.maxLength(this.maxLengthComment)]],
                hasInsurance: [false, [Validators.required]],
                insuranceComment: [null, [(control: UntypedFormControl) => {

                    if(!control.parent){

                        return null;
                    }

                    const hasInsuranceControl: FormControl = control.parent.get('hasInsurance') as FormControl;

                    if(!hasInsuranceControl){

                        return null;
                    }

                    const hasInsurance: boolean = hasInsuranceControl.value;

                    if(hasInsurance){

                        return (control.value as boolean) ? null : {
                            'isRequired': {
                                valid: false
                            }
                        };
                    }
                    else{

                        return null;
                    }
                }]]
            }),
            customer: this._formBuilder.group({
                spokenLanguage: [this.currentUser.locale, [Validators.required]],
                otherSpokenLanguages: [null, [(control: FormControl) => {

                    if(!control.parent){

                        return null;
                    }

                    const spokenLanguage: string = control.parent.get('spokenLanguage').value;

                    if(!spokenLanguage || !Boolean(spokenLanguage.length)){

                        return null;
                    }

                    if(spokenLanguage !== this.otherSpokenLanguagesIdentifier){

                        return null;
                    }

                    if(!!control.value && Boolean(control.value.length)){

                        return null;
                    }

                    return {
                        'isRequired': {
                            valid: false
                        }
                    };
                }]],
                comment: [null, [Validators.required, Validators.maxLength(this.maxLengthComment)]],
                country: [null, [Validators.required]]
            }),
            pricing: this._formBuilder.group({
                currency: [null, [Validators.required]],
                budget: [null, [Validators.required, Validators.pattern(REGEX_ONLY_NUMBER)]],
                comment: [null, [Validators.maxLength(this.maxLengthComment)]]
            })
        });

        this.formService.submitCallback = (): void => {

            if(this.form.invalid){

                return;
            }

            const formValue = {...this.form.getRawValue()};

            const data: object = Object.assign(formValue, {
                offerCreators: (formValue.offerCreators as Society[]).map((offerCreator: Society): { offerCreator: Society } => {

                    return {
                        offerCreator: offerCreator
                    };
                }),
                closedAt: (formValue.closedAt as Moment).format(DATE_FORMAT),
                composition: Object.assign(formValue.composition, {
                    dateStart: formValue.composition.dateStart ? (formValue.composition.dateStart as Moment).format(DATE_FORMAT) : null,
                    dateEnd: formValue.composition.dateEnd ? (formValue.composition.dateEnd as Moment).format(DATE_FORMAT) : null,
                    nbAdult: formValue.composition.nbAdult ? parseInt(formValue.composition.nbAdult) : 0,
                    nbChild: formValue.composition.nbChild ? parseInt(formValue.composition.nbChild) : 0
                }),
                configuration: Object.assign(formValue.configuration, {
                    regions: (formValue.configuration.regions as OfferAttribute[]).filter((item: OfferAttribute): boolean => {

                        return (item.type.tag === 'region');
                    }),
                    administrativeAreas: (formValue.configuration.regions as OfferAttribute[]).filter((item: OfferAttribute): boolean => {

                        return (item.type.tag === 'administrativeArea');
                    }),
                    accommodations: (formValue.configuration.accommodations as OfferAttribute[]).filter((item: OfferAttribute): boolean => {

                        return item.id !== null;
                    }),
                    restoration: formValue.configuration.restoration.id ? formValue.configuration.restoration : null
                }),
                customer: Object.assign(formValue.customer, {
                    country: (formValue.customer.country as Country).code
                })
            });

            this._quotationService.createItemAPI(data).subscribe((): void => {

                this.setActiveViews(['confirmation']);
            });
        };
    }

    private _initEvents(): void {

        this.form.valueChanges.subscribe(this._updatePanelsValidation.bind(this));

        this.offerCreatorSearchService.selectedOfferCreators$.subscribe((selectedItems: Society[]): void => {

            this.form.get('offerCreators').patchValue(selectedItems);

            this.offerCreatorSearchService.selectOfferCreatorAllowed = (this.maxOfferCreatorSelectionAllowed === null || selectedItems.length < this.maxOfferCreatorSelectionAllowed);

            this._updateCustomerTypologies();

            this._updateRegions();
        });

        this.configurationControl.get('customerTypology').valueChanges.subscribe((): void => {

            this.configurationControl.get('allowGiftVoucher').updateValueAndValidity();

            this.configurationControl.get('allowGiftVoucher').markAsTouched();
        });

        this.configurationControl.get('hasInsurance').valueChanges.subscribe((): void => {

            this.configurationControl.get('insuranceComment').patchValue(null);

            this.configurationControl.get('insuranceComment').updateValueAndValidity();

            this.configurationControl.get('insuranceComment').markAsTouched();
        });

        this.configurationControl.get('allowGiftVoucher').valueChanges.subscribe((): void => {

            this.compositionControl.get('dateStart').patchValue(null);

            this.compositionControl.get('dateStart').updateValueAndValidity();

            this.compositionControl.get('dateStart').markAsTouched();

            this.compositionControl.get('dateEnd').patchValue(null);

            this.compositionControl.get('dateEnd').updateValueAndValidity();

            this.compositionControl.get('dateEnd').markAsTouched();

            this.compositionControl.get('flexibility').patchValue(null);

            this.compositionControl.get('flexibility').updateValueAndValidity();

            this.compositionControl.get('flexibility').markAsTouched();
        });

        this.compositionControl.get('dateStart').valueChanges.subscribe((dateStart: Moment): void => {

            this.compositionControl.get('dateEnd').patchValue(null);

            this.minStayDateEnd = dateStart;
        });

        this.compositionControl.get('nbAdult').valueChanges.subscribe((): void => {

            this.compositionControl.get('nbChild').updateValueAndValidity({onlySelf: true, emitEvent: false});
        });

        this.compositionControl.get('nbChild').valueChanges.subscribe((): void => {

            this.compositionControl.get('nbAdult').updateValueAndValidity({onlySelf: true, emitEvent: false});
        });

        this.customerControl.get('spokenLanguage').valueChanges.subscribe((): void => {

            this.customerControl.get('otherSpokenLanguages').patchValue(null);

            this.customerControl.get('otherSpokenLanguages').updateValueAndValidity();

            this.customerControl.get('otherSpokenLanguages').markAsTouched();
        });
    }

    private _initPanels(): void {

        const items: { tag: FormPanelTag, controls: AbstractControl[] }[]  = [
            {
                tag: 'date',
                controls: [
                    this.compositionControl.get('dateStart'),
                    this.compositionControl.get('dateEnd'),
                    this.compositionControl.get('flexibility'),
                    this.compositionControl.get('duration'),
                    this.configurationControl.get('regions'),
                    this.configurationControl.get('regionComment')
                ]
            },
            {
                tag: 'composition',
                controls: [
                    this.compositionControl.get('nbAdult'),
                    this.compositionControl.get('nbChild'),
                    this.compositionControl.get('childrenAgeComment')
                ]
            },
            {
                tag: 'budget',
                controls: [
                    this.pricingControl.get('budget'),
                    this.pricingControl.get('currency'),
                    this.pricingControl.get('comment')
                ]
            },
            {
                tag: 'wish',
                controls: [
                    this.configurationControl.get('accommodations'),
                    this.configurationControl.get('accommodationsComment'),
                    this.configurationControl.get('restoration'),
                    this.configurationControl.get('restorationComment'),
                    this.configurationControl.get('transportComment'),
                    this.configurationControl.get('activities')
                ]
            },
            {
                tag: 'assurance',
                controls: [
                    this.configurationControl.get('hasInsurance'),
                    this.configurationControl.get('insuranceComment')
                ]
            },
            {
                tag: 'more',
                controls: [
                    this.customerControl.get('comment'),
                    this.customerControl.get('spokenLanguage'),
                    this.customerControl.get('otherSpokenLanguages'),
                    this.customerControl.get('country'),
                    this.form.get('closedAt')
                ]
            }
        ];

        this.panels = items.map((item: FormPanel<FormPanelTag>, index: number): FormPanel<FormPanelTag> => {

            return Object.assign(item, {
                valid: false,
                open: index === 0
            } as Partial<FormPanel<FormPanelTag>>);
        });

        this._updatePanelsValidation();
    }

    private _updatePanelsValidation(): void {

        this.panels.forEach((item: FormPanel<FormPanelTag>): void => {

            item.valid = item.controls.every((control: AbstractControl): boolean => {

                return control.valid;
            });
        });
    }

    private _updateCustomerTypologies(): void {

        // Réinitialisation de la cible clientèle sélectionnée

        this.configurationControl.get('customerTypology').patchValue(null,{ onlySelf: true, emitEvent: false });

        // Récupération des cibles clientèles des créateurs d'offres

        const offerCreatorCustomerTypologies: CustomerTypology[] = [];

        (this.form.get('offerCreators').value as Society[]).forEach((item: Society): void => {

            offerCreatorCustomerTypologies.push(...item.details.customerTypologies);
        });

        this.availableCustomerTypologies = uniqueArrayOfObjectsByProperty(offerCreatorCustomerTypologies, 'id');
    }

    private _updateRegions(): void {

        if(!(this.form.get('offerCreators').value as Society[]).length){

            return;
        }

        // Réinitialisation des régions sélectionnées

        this.configurationControl.get('regions').patchValue([]);

        // Récupération des régions des créateurs d'offres

        const isAllRegions: boolean = (this.form.get('offerCreators').value as Society[]).some((item: Society): boolean => {

            return item.details.allRegions;
        });

        if(isAllRegions){

            this.regionSelectGroupComponent.parentOptions = this.regions;
        }
        else{

            const offerCreatorRegions: OfferAttribute[] = [];

            (this.form.get('offerCreators').value as Society[]).forEach((item: Society): void => {

                offerCreatorRegions.push(...item.details.regions);
            });

            this.regionSelectGroupComponent.parentOptions = uniqueArrayOfObjectsByProperty(offerCreatorRegions, 'id');
        }
    }

    public compareById(a: { id: number }, b: { id: number }): boolean {

        if(!a || !b){

            return false;
        }

        return a.id === b.id;
    }

    public redirectToOrigin(): void {

        switch (this.origin.key){

            case 'catalog':

                this._router.navigate(['account/offer/list/catalog']);

                break;

            case 'offerCreatorList':

                this._router.navigate(['account/offer-creator/list']);

                break;

            case 'offerCreatorRead':

                this._router.navigate(['account/offer-creator/read', this._activatedRoute.snapshot.queryParams['offerCreatorId']]);

                break;
        }
    }

    public resetOfferCreatorSelection(): void {

        this.offerCreatorSearchService.selectedOfferCreators$.next([]);
    }

    public getAttributeObservable(tag: OfferAttributeTypeTagType): Observable<OfferAttribute[]> {

        const match: AttributeObservableData = this.attributeObservables$.find((attributeObservable: AttributeObservableData ): boolean => {

            return attributeObservable.tag === tag;
        });

        return match ? match.observable : of([]);
    }

    public parseAttributes(attributes: OfferAttribute[]): string {

        return attributes.map((attribute: OfferAttribute): string => {

            if(attribute.id === null){

                return this._translateService.instant('withoutPreference.value');
            }

            return this.translationService.getFallbackTranslation(attribute.translations).label;

        }).join(', ');
    }

    public isActiveView(view: TemplateView): boolean{

        return this.activeViews.includes(view);
    }

    public setActiveViews(activeViews: TemplateView[]): void {

        this.activeViews = activeViews;
    }

    public redirectToList(): void {

        this._router.navigate(['account/quotation/list']);
    }

    public openLeaveWithoutSubmitDialog(): void {

        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this._dialog.open(ConfirmDialogComponent, {
            width: '500px',
            data: {
                title: this._translateService.instant('quotation.leaveWithoutSubmit.value'),
                content: this._translateService.instant('quotation.leaveWithoutSubmit.description.value')
            } as ConfirmDialogData
        });

        dialogRef.componentInstance.confirm.subscribe((): void => {

            this.redirectToOrigin();
        });
    }

    public handleAccommodation(option: MatOption): void {

        const isNoneAccommodationOption: boolean = option.value.id === null;

        if(isNoneAccommodationOption){

            this.configurationControl.get('accommodations').patchValue(option.selected ? [{id: null}] : []);
        }
        else{

            const currentAccommodations: { id: number }[] = [...(this.configurationControl.get('accommodations').value as OfferAttribute[])].filter((item: OfferAttribute): boolean => {

                return item.id !== null;
            });

            this.configurationControl.get('accommodations').patchValue(currentAccommodations);
        }
    }

    public getPanel(tag: FormPanelTag): FormPanel<FormPanelTag> {

        return this.panels.find((panel: FormPanel<FormPanelTag>): boolean => {

            return panel.tag === tag;
        });
    }

    public isValidPanel(tag: FormPanelTag): boolean {

        return this.getPanel(tag).valid;
    }

    public isOpenPanel(tag: FormPanelTag): boolean {

        return this.getPanel(tag).open;
    }

    public togglePanelOpening(tag: FormPanelTag): void {

        const panel: FormPanel<FormPanelTag> = this.getPanel(tag);

        panel.open = !panel.open;
    }

    get form(): FormGroup {

        return this.formService.form;
    }

    get configurationControl(): FormGroup {

        return this.form.get('configuration') as FormGroup;
    }

    get compositionControl(): FormGroup {

        return this.form.get('composition') as FormGroup;
    }

    get customerControl(): FormGroup {

        return this.form.get('customer') as FormGroup;
    }

    get pricingControl(): FormGroup {

        return this.form.get('pricing') as FormGroup;
    }

    get formTabValidationItemsCallback(): () => FormTabValidationItem[] {

        return (): FormTabValidationItem[] => {

            const items: Partial<FormTabValidationItem>[] = [];

            // Créateurs d'offres

            items.push({
                tag: 'offerCreators',
                controls: [
                    this.form.get('offerCreators')
                ]
            });

            // Données du formulaire

            items.push({
                tag: 'formData',
                controls: [
                    this.form.get('ownerReference'),
                    this.form.get('closedAt'),
                    this.configurationControl,
                    this.compositionControl,
                    this.customerControl,
                    this.pricingControl
                ]
            });

            // Récapitulatif et envoi

            items.push({
                tag: 'summary',
                controls: []
            });

            return items.map((item: Partial<FormTabValidationItem>, index: number): FormTabValidationItem => {

                return Object.assign(item, {
                    position: index + 1
                } as FormTabValidationItem);
            });
        };
    }

    get hasChild(): boolean {

        const control: FormControl = this.compositionControl.get('nbChild') as FormControl;

        return Boolean(parseInt(control.value));
    }

    get attributeTypeTags(): OfferAttributeTypeTagType[] {

        return ['accommodations', 'restoration', 'activities'];
    }

    get quotation(): Quotation {

        return Object.assign(this.form.getRawValue(), {
            customer: Object.assign(this.customerControl.getRawValue(), {
                spokenLanguage: (this.customerControl.get('spokenLanguage').value === this.otherSpokenLanguagesIdentifier) ? null : this.customerControl.get('spokenLanguage').value
            })
        }) as Quotation;
    }

    get selectedOfferCreators(): Society[] {

        return this.form.get('offerCreators').value as Society[];
    }

    get loadItemsSourceCallback(): (params?: string[]) => Observable<Pagination<Society>> {

        return (params?: string[]): Observable<Pagination<Society>> => {

            return this._offerCreatorService.getPaginationItemsAPI(params);
        }
    }

    get nightIncluded(): boolean {

        if(!this.form || !this.compositionControl){

            return false;
        }

        const duration: OfferDuration = this.compositionControl.get('duration').value;

        return duration && duration.value > 1;
    }
}
